import { useEffect, useState, lazy, Suspense } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { getCurrentSubdomain, getCurrentCustomDomain } from "@/lib/subdomain";
import { supabase } from "@/integrations/supabase/client";
import {
    CustomDashboardDomainProvider,
    type CustomDashboardDomainInfo,
} from "@/contexts/CustomDashboardDomainContext";

const Storefront = lazy(() => import("@/pages/Storefront"));
const ReferralPortal = lazy(() => import("@/pages/portal/ReferralPortal"));
const CustomerPortal = lazy(() => import("@/pages/portal/CustomerPortal"));

interface SubdomainRouterProps {
  children: React.ReactNode;
}

/**
 * Detects if user is accessing via:
 * 1. Platform subdomain (e.g., freecoffee.noknok.pro)
 * 2. Custom domain (e.g., joescoffee.com, order.joescoffee.com)
 * 
 * Renders the storefront directly instead of the normal app routes.
 * However, allows checkout and order confirmation routes to use normal routing.
 */
const SubdomainRouter = ({ children }: SubdomainRouterProps) => {
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [customDomain, setCustomDomain] = useState<string | null>(null);
  const [resolvedSubdomain, setResolvedSubdomain] = useState<string | null>(null);
  // When set, render the partner portal scoped to this slug instead of the
  // storefront. Set when the platform subdomain matches a published
  // referral_programs row OR when a custom apex resolves to one.
  const [referralProgramSlug, setReferralProgramSlug] = useState<string | null>(null);
  // When set, the host matches a business's customer_portal_subdomain /
  // customer_portal_custom_domain (and the portal is enabled) — render the
  // white-label customer portal scoped to this business id.
  const [customerPortalBusinessId, setCustomerPortalBusinessId] = useState<string | null>(null);
  // When set, the request is on a business's dashboard_custom_domain — render
  // normal app routes (Auth + dashboard) but inject branding via context.
  const [dashboardBranding, setDashboardBranding] = useState<CustomDashboardDomainInfo | null>(null);
  // Branding fetched from a platform subdomain match — used when the visitor
  // is on {subdomain}.noknok.pro/auth or /business/dashboard/*. Same context
  // shape as the custom-domain branding; render-time decides which to inject.
  const [subdomainBranding, setSubdomainBranding] = useState<CustomDashboardDomainInfo | null>(null);
  const [checked, setChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkDomain = async () => {
      // First, check for platform subdomain (*.noknok.pro)
      const detectedSubdomain = getCurrentSubdomain();
      if (detectedSubdomain) {
        // Referral programs share the *.noknok.pro namespace with storefronts
        // and websites — check if this slug maps to a published program first
        // so the portal beats the storefront fallback.
        try {
          const { data: refProg } = await supabase
            .from("referral_programs" as never)
            .select("slug")
            .eq("slug", detectedSubdomain.toLowerCase())
            .eq("status", "published")
            .maybeSingle();
          if ((refProg as { slug?: string } | null)?.slug) {
            setReferralProgramSlug((refProg as { slug: string }).slug);
            setChecked(true);
            return;
          }
        } catch (err) {
          console.error("[SubdomainRouter] referral program lookup failed:", err);
        }

        // Customer portal subdomain — a dedicated portal host beats the storefront
        // fallback (same precedence pattern as referral programs above).
        try {
          const { data: portalBiz } = await supabase
            .from("businesses_safe")
            .select("id, customer_portal_enabled")
            .eq("customer_portal_subdomain", detectedSubdomain.toLowerCase())
            .maybeSingle();
          const pRow = portalBiz as { id?: string; customer_portal_enabled?: boolean } | null;
          if (pRow?.id && pRow.customer_portal_enabled) {
            setCustomerPortalBusinessId(pRow.id);
            setChecked(true);
            return;
          }
        } catch (err) {
          console.error("[SubdomainRouter] customer portal lookup failed:", err);
        }

        setSubdomain(detectedSubdomain);

        // Fetch branding info from the business that owns this subdomain so
        // {subdomain}.noknok.pro/auth and /business/dashboard/* can render
        // branded. The lookup runs once on mount; render-time decides whether
        // to inject the context (dashboard paths) or render the storefront.
        try {
          const sub = detectedSubdomain.toLowerCase();
          // Match the host against BOTH the storefront subdomain and the
          // dedicated CRM-login subdomain (dashboard_subdomain). They're
          // distinct names, so at most one business matches.
          const { data: biz } = await supabase
            .from("businesses_safe")
            .select("id, business_name, subdomain, dashboard_subdomain, logo_url, favicon_url, primary_color")
            .or(`subdomain.eq.${sub},dashboard_subdomain.eq.${sub}`)
            .maybeSingle();
          const row = biz as
            | {
                id: string;
                business_name: string | null;
                subdomain: string | null;
                dashboard_subdomain: string | null;
                logo_url: string | null;
                favicon_url: string | null;
                primary_color: string | null;
              }
            | null;
          if (row?.id) {
            const branding = {
              businessId: row.id,
              businessName: row.business_name ?? "",
              subdomain: row.subdomain,
              logoUrl: row.logo_url,
              faviconUrl: row.favicon_url,
              primaryColor: row.primary_color,
            };
            // If the host is the business's DEDICATED CRM-login subdomain (and
            // not its storefront subdomain), treat it exactly like a dashboard
            // custom domain: no storefront surface, every non-dashboard path
            // redirects to the branded /auth login.
            const isDashboardHost =
              row.dashboard_subdomain?.toLowerCase() === sub &&
              row.subdomain?.toLowerCase() !== sub;
            if (isDashboardHost) {
              setDashboardBranding(branding);
              setChecked(true);
              return;
            }
            setSubdomainBranding(branding);
          }
        } catch (err) {
          console.error("[SubdomainRouter] subdomain branding lookup failed:", err);
        }

        setChecked(true);
        return;
      }

      // Then, check for custom domain
      const detectedCustomDomain = getCurrentCustomDomain();
      if (detectedCustomDomain) {
        setCustomDomain(detectedCustomDomain);

        // Look up the business by custom_domain to get its subdomain
        // Also try without www prefix (www.example.com -> example.com)
        const domainVariants = [detectedCustomDomain];
        if (detectedCustomDomain.startsWith('www.')) {
          domainVariants.push(detectedCustomDomain.replace('www.', ''));
        }

        try {
          // Referral programs may also bind a custom apex — check first.
          for (const domain of domainVariants) {
            const { data: refProg } = await supabase
              .from("referral_programs" as never)
              .select("slug")
              .eq("custom_domain", domain)
              .eq("custom_domain_verified", true)
              .eq("status", "published")
              .maybeSingle();
            if ((refProg as { slug?: string } | null)?.slug) {
              setReferralProgramSlug((refProg as { slug: string }).slug);
              setChecked(true);
              return;
            }
          }

          // Dashboard custom domain — branded CRM login. Match BEFORE the
          // storefront fallback so a single business can run a separate
          // storefront domain + CRM domain side-by-side without collision.
          for (const domain of domainVariants) {
            const { data: bizDash } = await supabase
              .from("businesses_safe")
              .select("id, business_name, subdomain, logo_url, favicon_url, primary_color, dashboard_custom_domain")
              .eq("dashboard_custom_domain", domain)
              .maybeSingle();
            const row = bizDash as
              | {
                  id: string;
                  business_name: string | null;
                  subdomain: string | null;
                  logo_url: string | null;
                  favicon_url: string | null;
                  primary_color: string | null;
                  dashboard_custom_domain: string | null;
                }
              | null;
            if (row?.id) {
              setDashboardBranding({
                businessId: row.id,
                businessName: row.business_name ?? "",
                subdomain: row.subdomain,
                logoUrl: row.logo_url,
                faviconUrl: row.favicon_url,
                primaryColor: row.primary_color,
              });
              setChecked(true);
              return;
            }
          }

          // Customer portal custom domain — branded customer portal. Match BEFORE
          // the storefront fallback so a tenant can run a storefront domain + a
          // portal domain side-by-side without collision.
          for (const domain of domainVariants) {
            const { data: portalBiz } = await supabase
              .from("businesses_safe")
              .select("id, customer_portal_enabled, customer_portal_custom_domain")
              .eq("customer_portal_custom_domain", domain)
              .maybeSingle();
            const pRow = portalBiz as { id?: string; customer_portal_enabled?: boolean } | null;
            if (pRow?.id && pRow.customer_portal_enabled) {
              setCustomerPortalBusinessId(pRow.id);
              setChecked(true);
              return;
            }
          }

          let foundBusiness = null;
          for (const domain of domainVariants) {
            const { data: business } = await supabase
              .from("businesses_safe")
              .select("subdomain")
              .eq("custom_domain", domain)
              .maybeSingle();

            if (business?.subdomain) {
              foundBusiness = business;
              break;
            }
          }

          if (foundBusiness?.subdomain) {
            setResolvedSubdomain(foundBusiness.subdomain);
          }
        } catch (err) {
          console.error("Error looking up custom domain:", err);
        }

        setChecked(true);
        return;
      }

      // No subdomain or custom domain detected
      setChecked(true);
    };

    checkDomain();
  }, []);

  // Wait for domain check before rendering. Show a neutral spinner (never the
  // NokNok mark) while we figure out which business this host belongs to — the
  // business's own icon appears once branding resolves. Matches the spinner-only
  // boot shell in index.html so there's no flash of blank or NokNok branding.
  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-7 w-7 rounded-full border-[3px] border-primary/20 border-t-primary animate-spin" />
      </div>
    );
  }

  // Helper to check if current path needs normal routing (checkout, order confirmation, etc.)
  const needsNormalRouting = () => {
    const path = location.pathname;
    // Allow checkout, order confirmation, orders, claim, hosted-form, and the
    // public share routes (form submission /s/, shared file/folder /sf/, shared
    // lead /share/) to use normal routing — these public links must render on
    // ANY host they were minted on, including a business's storefront subdomain.
    return path.includes('/checkout') ||
      path.includes('/order/') ||
      path.includes('/orders') ||
      path.includes('/claim/') ||
      path.includes('/card/') ||
      path.startsWith('/f/') ||
      path.startsWith('/q/') ||
      path.startsWith('/s/') ||
      path.startsWith('/sf/') ||
      path.startsWith('/share/');
  };

  // Dashboard paths skip the storefront on platform subdomains: the visitor
  // sees the branded CRM login at {subdomain}.noknok.pro/auth and the
  // dashboard at /business/dashboard/*. Branding comes from subdomainBranding.
  const isDashboardPath = () => {
    const path = location.pathname;
    return path === '/auth' ||
      path === '/login' ||
      path.startsWith('/auth/') ||
      path.startsWith('/login/') ||
      path.startsWith('/business/dashboard') ||
      path.startsWith('/dashboard');
  };

  // If the host is a business's branded CRM-login custom domain, render the
  // normal app routes (Auth + dashboard) but expose the branding via context
  // so children can swap logos, hide platform chrome, and skip the picker.
  if (dashboardBranding) {
    // CRM-login hosts have no marketing / storefront surface — every
    // non-dashboard path redirects to the branded login. EXCEPT public-surface
    // paths that must work on any host: hosted forms (/f/…) ride on the CRM
    // subdomain (clearhome.noknok.pro/f/{token}), digital cards (/card/…), etc.
    // Those render normally; only the bare marketing/storefront gets bounced.
    if (!isDashboardPath() && !needsNormalRouting()) {
      return <Navigate to="/auth" replace />;
    }
    return (
      <CustomDashboardDomainProvider value={dashboardBranding}>
        {children}
      </CustomDashboardDomainProvider>
    );
  }

  // If the host maps to a published referral program, render the partner portal.
  // Beats the storefront/websites checks because the worker resolution order
  // already prioritized referral programs.
  if (referralProgramSlug) {
    return (
      <Suspense fallback={null}>
        <ReferralPortal slugOverride={referralProgramSlug} />
      </Suspense>
    );
  }

  // If the host maps to a business's enabled customer portal, render it. Beats
  // the storefront fallback (a portal host is distinct from the storefront host).
  if (customerPortalBusinessId) {
    return (
      <Suspense fallback={null}>
        <CustomerPortal businessIdOverride={customerPortalBusinessId} />
      </Suspense>
    );
  }

  // If platform subdomain detected
  if (subdomain) {
    // Dashboard paths on a platform subdomain → branded CRM login + dashboard
    // shell. Inject the same context the custom-domain flow uses; the worker
    // already passes these paths through without /store/{subdomain} rewriting.
    if (isDashboardPath() && subdomainBranding) {
      return (
        <CustomDashboardDomainProvider value={subdomainBranding}>
          {children}
        </CustomDashboardDomainProvider>
      );
    }

    // Allow checkout and order routes to go through normal routing
    if (needsNormalRouting()) {
      return <>{children}</>;
    }

    // Redirect /store/* paths to root on subdomain (except special routes)
    if (window.location.pathname.startsWith('/store/')) {
      window.location.replace('/');
      return null;
    }
    return <Suspense fallback={null}><Storefront subdomainOverride={subdomain} /></Suspense>;
  }

  // If custom domain detected and resolved to a business
  if (customDomain && resolvedSubdomain) {
    // Allow checkout and order routes to go through normal routing
    if (needsNormalRouting()) {
      return <>{children}</>;
    }

    // Redirect /store/* paths to root on custom domain
    if (window.location.pathname.startsWith('/store/')) {
      window.location.replace('/');
      return null;
    }
    return <Suspense fallback={null}><Storefront subdomainOverride={resolvedSubdomain} /></Suspense>;
  }

  // If custom domain detected but not found in database, still let the
  // public-surface paths through (hosted forms ride on their own custom
  // domain — forms.acme.com/f/{token} — which is NOT a storefront domain, so
  // resolvedSubdomain is null here; /f/ is in needsNormalRouting()).
  if (customDomain && !resolvedSubdomain) {
    if (needsNormalRouting()) {
      return <>{children}</>;
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <h1 className="text-2xl font-bold mb-2">Domain Not Configured</h1>
        <p className="text-muted-foreground text-center max-w-md">
          The domain <strong>{customDomain}</strong> is not yet connected to a store.
          Please check your domain settings.
        </p>
      </div>
    );
  }

  // Otherwise, render normal app routes
  return <>{children}</>;
};

export default SubdomainRouter;

